import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plane, MapPin, ChevronRight, Check, ChevronsUpDown, LogOut, Clock, UtensilsCrossed } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { airports, terminals } from "@/data/airports";
import MobileNav from "@/components/MobileNav";
import { NearbyAirportBanner } from "@/components/NearbyAirportBanner";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const Index = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { toast } = useToast();
  const [selectedAirport, setSelectedAirport] = useState<string>("");
  const [selectedTerminal, setSelectedTerminal] = useState<string>("");
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [openAirport, setOpenAirport] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
    navigate("/auth");
  };

  const isFormValid = selectedAirport && selectedTerminal;

  const handleContinue = () => {
    if (isFormValid) {
      const selectedAirportData = airports.find(a => a.code === selectedAirport);
      const airportDisplayName = selectedAirportData 
        ? `${selectedAirportData.code} - ${selectedAirportData.name}`
        : selectedAirport;
      navigate("/dashboard", { 
        state: { 
          airport: airportDisplayName, 
          terminal: selectedTerminal,
          flightNumber: flightNumber || undefined
        } 
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-primary/5 pb-20 md:pb-0">
      <div className="container max-w-lg mx-auto px-4 py-8 md:py-12 flex flex-col items-center justify-center min-h-screen">
        {/* Navigation Buttons - Desktop only */}
        <div className="absolute top-4 left-4 right-4 hidden md:flex justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="gap-2 min-h-[44px]"
          >
            ← Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="gap-2 min-h-[44px]"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign Out
          </Button>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-8 md:mb-12 space-y-4">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-lg mb-4">
            <Plane className="h-10 w-10 md:h-12 md:w-12 text-primary-foreground" aria-hidden="true" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">
            Airport Buddy
          </h1>
          <p className="text-muted-foreground text-base md:text-lg px-4">
            Your real-time airport assistant for stress-free travel
          </p>
        </div>

        {/* Nearby Airport Detection */}
        <div className="w-full max-w-md mx-auto mb-4">
          <NearbyAirportBanner />
        </div>

        {/* Selection Card */}
        <Card className="w-full p-5 md:p-6 shadow-lg border-border/50 space-y-5 md:space-y-6">
          <div className="space-y-2">
            <label htmlFor="airport-select" className="text-base font-medium text-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
              Select Your Airport
            </label>
            <Popover open={openAirport} onOpenChange={setOpenAirport}>
              <PopoverTrigger asChild>
                <Button
                  id="airport-select"
                  variant="outline"
                  role="combobox"
                  aria-expanded={openAirport}
                  aria-label="Select airport"
                  className="w-full justify-between bg-background min-h-[48px] text-base"
                >
                  {selectedAirport
                    ? airports.find((airport) => airport.code === selectedAirport)?.code + " - " + 
                      airports.find((airport) => airport.code === selectedAirport)?.name
                    : "Search airports..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[calc(100vw-2rem)] max-w-lg p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search airports..." className="h-12" />
                  <CommandList className="max-h-[50vh]">
                    <CommandEmpty>No airport found.</CommandEmpty>
                    <CommandGroup>
                      {airports.map((airport) => (
                        <CommandItem
                          key={airport.code}
                          value={`${airport.code} ${airport.name} ${airport.city}`}
                          onSelect={() => {
                            setSelectedAirport(airport.code);
                            setOpenAirport(false);
                          }}
                          className="min-h-[52px] py-3 cursor-pointer"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 flex-shrink-0",
                              selectedAirport === airport.code ? "opacity-100" : "opacity-0"
                            )}
                            aria-hidden="true"
                          />
                          <div className="flex flex-col">
                            <span className="font-semibold text-base">{airport.code}</span>
                            <span className="text-sm text-muted-foreground">
                              {airport.name} · {airport.city}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <label htmlFor="terminal-select" className="text-base font-medium text-foreground flex items-center gap-2">
              <Plane className="h-4 w-4 text-primary" aria-hidden="true" />
              Select Terminal
            </label>
            <Select 
              value={selectedTerminal} 
              onValueChange={setSelectedTerminal}
              disabled={!selectedAirport}
            >
              <SelectTrigger 
                id="terminal-select"
                className="w-full bg-background min-h-[48px] text-base"
                aria-label="Select terminal"
              >
                <SelectValue placeholder="Choose terminal..." />
              </SelectTrigger>
              <SelectContent>
                {terminals.map((terminal) => (
                  <SelectItem key={terminal} value={terminal} className="min-h-[44px] py-3">
                    Terminal {terminal}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="flight-number" className="text-base font-medium text-foreground flex items-center gap-2">
              <Plane className="h-4 w-4 text-primary" aria-hidden="true" />
              Flight Number (Optional)
            </label>
            <Input
              id="flight-number"
              type="text"
              placeholder="e.g., AA1234 or UA 5678"
              value={flightNumber}
              onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
              maxLength={10}
              className="bg-background"
              aria-describedby="flight-hint"
            />
            <p id="flight-hint" className="text-sm text-muted-foreground">
              Enter your flight number to track your flight status
            </p>
          </div>

          <Button
            className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground shadow-lg gap-2 min-h-[52px] text-base"
            size="lg"
            onClick={handleContinue}
            disabled={!isFormValid}
          >
            Continue
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </Button>
        </Card>

        {/* Features Preview */}
        <div className="grid grid-cols-3 gap-3 md:gap-4 mt-8 md:mt-12 w-full">
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 md:p-4 bg-primary/10 rounded-xl">
              <Clock className="h-5 w-5 md:h-6 md:w-6 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">TSA Wait Times</p>
          </div>
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 md:p-4 bg-primary/10 rounded-xl">
              <Plane className="h-5 w-5 md:h-6 md:w-6 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">Flight Status</p>
          </div>
          <div className="text-center space-y-2">
            <div className="inline-flex p-3 md:p-4 bg-primary/10 rounded-xl">
              <UtensilsCrossed className="h-5 w-5 md:h-6 md:w-6 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm text-muted-foreground font-medium">Restaurants</p>
          </div>
        </div>

        {/* Get the native app */}
        <div className="w-full mt-8">
          <AppStoreCTA
            placement="home_picker"
            headline="Get this on your phone"
            subline="Live TSA waits, a leave-by timer, and gate-change alerts — free on iPhone."
          />
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileNav activeTab="home" />
    </div>
  );
};

export default Index;
